import shallow from "zustand/shallow";
import { TextFieldWithLabel } from "../../../../../shared/ui/TextFieldWithLabel";
import { SliderWithInput } from "../../../../../shared/ui";
import styles from "../../DimensionsPanel.module.scss";
import { useShelvingStore } from "../../../../../../store/useShelvingStore";
import { useStoreRoom, useStoreSystem } from "../../../../../../store";
import { getInfoSections } from "./utils";
import { shelfConstants } from "../../../../../../constants";
import { useEffect } from "react";
import { getWallLineVertices } from "../../../../../SceneObjects/ButtonsScene/utils";
import { getCameraPositionToMiddleLine, getTargetPositionToMiddleLine } from "../../../../../../store/helpers";
import { SideTypeWithoutTop } from "../../../../../../store/types/store";
import { useAlerts } from "../../../../../../hooks/useAlerts";

export const SectionsParamsTab = () => {
    const { shelf, setPropertyShelf, updateItemsYPositionsAfterResizeRoomHeight } = useShelvingStore(
        (state) => ({
            shelf: state.shelving,
            setPropertyShelf: state.setPropertyShelving,
            updateItemsYPositionsAfterResizeRoomHeight: state.updateItemsYPositionsAfterResizeRoomHeight,
        }),
        shallow
    );

    const { roomHeight, roomLength, roomDepth } = useStoreRoom(
        (state) => ({
            roomHeight: state.height,
            roomLength: state.length,
            roomDepth: state.depth,
        }),
        shallow
    );

    const { currentSide, setSystemProperties } = useStoreSystem(
        (state) => ({
            currentSide: state.currentSide,
            setSystemProperties: state.setSystemProperties,
        }),
        shallow
    );

    const { addReadyAlert } = useAlerts();

    const currentShelf = currentSide === "top" ? shelf.front : shelf[currentSide];
    const wallName = currentShelf.name as SideTypeWithoutTop;

    const getMinMaxSize = () => {
        const infoSections = getInfoSections(currentShelf);
        const minLength =
            shelfConstants.minSize.length > infoSections.length ? shelfConstants.minSize.length : infoSections.length;
        const maxLength =
            (currentSide === "front" || currentSide === "back" ? roomLength : roomDepth) - currentShelf.sideIndent;
        const minHeight =
            shelfConstants.minSize.height > infoSections.minHeight
                ? shelfConstants.minSize.height
                : infoSections.minHeight;
        const maxHeight = roomHeight - currentShelf.offsetCeiling;
        const minSideIndent = shelfConstants.minSize.sideIndent;
        const maxSideIndent =
            (currentSide === "front" || currentSide === "back" ? roomLength : roomDepth) - currentShelf.length;
        const minOffsetCeiling = shelfConstants.minSize.offsetCeiling;
        const maxOffsetCeiling = roomHeight - currentShelf.height;

        return {
            minLength,
            maxLength,
            minHeight,
            maxHeight,
            minSideIndent,
            maxSideIndent,
            minOffsetCeiling,
            maxOffsetCeiling,
        };
    };

    const setNewShelfLength = (newLength: number) => {
        setPropertyShelf([{ wallName, property: "length", value: newLength }]);
    };

    const setNewShelfHeight = (newHeight: number) => {
        setPropertyShelf([{ wallName, property: "height", value: newHeight }]);
    };

    const setNewShelfIndent = (newIndent: number) => {
        let newValue = newIndent;
        setPropertyShelf([{ wallName, property: "sideIndent", value: newValue }]);

        if (newIndent > minMaxSize.maxSideIndent) {
            newValue = minMaxSize.maxSideIndent;
            addReadyAlert(
                `Стеллаж упирается в стену. Максимальный возможный размер ${minMaxSize.maxSideIndent}мм.`,
                "error",
                5000
            );
        }
        setPropertyShelf([{ wallName, property: "sideIndent", value: newValue }]);
    };

    const setNewOffsetCeiling = (newOffset: number) => {
        let newValue = newOffset;
        if (newOffset > minMaxSize.maxOffsetCeiling) {
            newValue = minMaxSize.maxOffsetCeiling;
            addReadyAlert(
                `Стеллаж упирается в пол. Максимальный возможный размер ${minMaxSize.maxOffsetCeiling}мм.`,
                "error",
                5000
            );
        }
        updateItemsYPositionsAfterResizeRoomHeight((-newValue + currentShelf.offsetCeiling) / 1000, wallName);
        setPropertyShelf([{ wallName, property: "offsetCeiling", value: newValue }]);
    };

    const minMaxSize = getMinMaxSize();

    useEffect(() => {
        const lineVertices = getWallLineVertices(currentSide);
        setSystemProperties([
            { name: "cameraPosition", value: getCameraPositionToMiddleLine(lineVertices) },
            { name: "targetCameraPosition", value: getTargetPositionToMiddleLine(lineVertices) },
        ]);
    }, [currentShelf]);

    return (
        <div className={styles.sectionsParams}>
            <SliderWithInput
                label={"Длина, мм"}
                min={minMaxSize.minLength}
                max={minMaxSize.maxLength}
                initialValue={currentShelf.length}
                onChange={setNewShelfLength}
            />
            <SliderWithInput
                label={"Высота, мм"}
                min={minMaxSize.minHeight}
                max={minMaxSize.maxHeight}
                initialValue={currentShelf.height}
                onChange={setNewShelfHeight}
            />
            <div className={styles.indents}>
                <TextFieldWithLabel
                    label={"Отступ слева, мм"}
                    min={minMaxSize.minSideIndent}
                    max={Infinity}
                    maxAfterBlur={minMaxSize.maxSideIndent}
                    initialValue={currentShelf.sideIndent}
                    onChange={setNewShelfIndent}
                />
                <TextFieldWithLabel
                    label={"Отступ сверху, мм"}
                    min={minMaxSize.minOffsetCeiling}
                    max={Infinity}
                    maxAfterBlur={minMaxSize.maxOffsetCeiling}
                    initialValue={currentShelf.offsetCeiling}
                    onChange={setNewOffsetCeiling}
                />
            </div>
        </div>
    );
};
